import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Loader } from '@/components/atoms/Loader';
import { Stack } from '@/components/atoms/Stack';
import { useMessages } from '@/hooks/messages';
import type { OmitSafely } from '@/types';
import { sortExpressions } from '@/utils/sorting';

import type { CostEstimationItemsTableProps } from '../CostEstimationItemsTable';
import CostEstimationItemsTable from '../CostEstimationItemsTable';
import { costEstimationItemsGroupedTablesMessageDefinitions } from './messages';

export type CostEstimationItemsGroupedTableProps = OmitSafely<
  CostEstimationItemsTableProps,
  'group'
> & {
  currencyCode?: string;
  hiddenCostTypeColumns?: string[];
};

const CostEstimationItemsGroupedTables: React.FC<CostEstimationItemsGroupedTableProps> = ({
  estimationItems,
  estimationModel,
  currencyCode,
  hiddenCostTypeColumns = [],
  ...rest
}: CostEstimationItemsGroupedTableProps) => {
  const messages = useMessages(costEstimationItemsGroupedTablesMessageDefinitions);

  const formModel = useWatch({ control: rest.control });

  const estimationItemsByGroupId = useMemo(() => {
    const filteredFormModelItems = formModel.estimationItems?.filter((x) => !!x.id) ?? [];
    if (!filteredFormModelItems) {
      return [];
    }
    return Object.groupBy(filteredFormModelItems, (i) => i.groupId!);
  }, [formModel.estimationItems]);

  const getEstimationModelItemGroup = useCallback(
    (estimationModelGroupId: string) =>
      estimationModel.itemGroups.find((x) => x.id === estimationModelGroupId),
    [estimationModel],
  );

  if (Object.keys(estimationItemsByGroupId).length === 0) {
    return (
      <>
        <p className='mt-4'>
          {messages.costUnit()}: {currencyCode ?? <Loader noMargin size='s' />}
        </p>
        <div className='mt-4 w-full overflow-x-auto' key='empty'>
          <CostEstimationItemsTable
            estimationItems={[]}
            estimationModel={estimationModel}
            {...rest}
            hiddenCostTypeColumns={hiddenCostTypeColumns}
          />
        </div>
      </>
    );
  }

  return (
    <div>
      <p className='mt-4'>
        {messages.costUnit()}: {currencyCode ?? <Loader noMargin size='s' />}
      </p>
      <Stack gap={10}>
        {Object.keys(estimationItemsByGroupId)
          .map(
            (estimationModelItemGroupId) =>
              getEstimationModelItemGroup(estimationModelItemGroupId)!,
          )
          .sort(sortExpressions.byOrder)
          .map((estimationModelGroup) => (
            <div className='mt-4 w-full overflow-x-auto' key={estimationModelGroup.id}>
              <CostEstimationItemsTable
                group={estimationModelGroup}
                estimationItems={estimationItems.filter(
                  (x) => x.estimationModelGroupId === estimationModelGroup.id,
                )}
                estimationModel={estimationModel}
                {...rest}
                hiddenCostTypeColumns={hiddenCostTypeColumns}
              />
            </div>
          ))}
      </Stack>
    </div>
  );
};

export default CostEstimationItemsGroupedTables;
